
import { MLCourse } from './types';

export const ML_ADVANCED_COURSE: MLCourse = {
  id: 'ml-advanced',
  title: 'Advanced Machine Learning',
  description: 'Master cutting-edge ML techniques including deep learning architectures, MLOps, and production deployment. Build enterprise-grade ML systems.',
  level: 'advanced',
  totalDuration: '18-22 hours',
  image: '/api/placeholder/400/300',
  prerequisites: [
    'Completed Intermediate ML course',
    'Strong Python programming skills',
    'Experience with TensorFlow or PyTorch',
    'Understanding of linear algebra and calculus',
    'Familiarity with cloud platforms (AWS/GCP/Azure)'
  ],
  learningOutcomes: [
    'Architect and train deep learning models',
    'Implement state-of-the-art NLP with transformers',
    'Build production ML pipelines with MLOps',
    'Deploy scalable ML systems in the cloud',
    'Apply reinforcement learning to real problems',
    'Create explainable and interpretable AI systems',
    'Optimize models for performance and efficiency',
    'Lead ML projects from conception to production'
  ],
  modules: [
    {
      id: 'ml-deep-architectures',
      title: 'Advanced Deep Learning Architectures',
      description: 'Master CNNs, RNNs, LSTMs, Transformers, and other state-of-the-art deep learning architectures.',
      duration: '3 hours',
      difficulty: 'advanced',
      objectives: [
        'Design and implement CNN architectures',
        'Build RNNs and LSTMs for sequence data',
        'Understand and apply transformer models',
        'Optimize deep learning model performance'
      ],
      content: `# Advanced Deep Learning Architectures

Modern deep learning relies on specialized architectures designed for specific types of data and tasks.

## Convolutional Neural Networks (CNNs)

### Architecture Components:
- **Convolutional Layers**: Feature extraction using filters
- **Pooling Layers**: Spatial dimension reduction
- **Fully Connected Layers**: Final classification/regression

### Key Concepts:
- **Filters/Kernels**: Small matrices that detect features
- **Stride**: Step size for filter movement
- **Padding**: Border handling strategy
- **Feature Maps**: Output of convolution operations

### Popular CNN Architectures:
- **LeNet**: Historical significance, MNIST digits
- **AlexNet**: ImageNet breakthrough, ReLU activation
- **VGG**: Very deep networks, small filters
- **ResNet**: Skip connections, ultra-deep networks
- **EfficientNet**: Optimal scaling of depth, width, resolution

## Recurrent Neural Networks (RNNs)

### Standard RNN:
- Processes sequences step by step
- Has memory of previous states
- Suffers from vanishing gradient problem

### LSTM (Long Short-Term Memory):
- Designed to remember long-term dependencies
- Uses gates to control information flow
- **Forget Gate**: What to forget from cell state
- **Input Gate**: What new information to store
- **Output Gate**: What to output based on cell state

### GRU (Gated Recurrent Unit):
- Simplified version of LSTM
- Combines forget and input gates
- Often performs similarly to LSTM with fewer parameters

## Transformer Architecture

### Key Innovation:
- **Self-Attention Mechanism**: Directly connect all positions
- **Parallel Processing**: More efficient than RNNs
- **Positional Encoding**: Add position information

### Components:
- **Multi-Head Attention**: Multiple attention mechanisms in parallel
- **Feed-Forward Networks**: Position-wise fully connected layers
- **Layer Normalization**: Stabilize training
- **Residual Connections**: Enable very deep networks

### Applications:
- **BERT**: Bidirectional encoder representations
- **GPT**: Generative pre-trained transformer
- **T5**: Text-to-text transfer transformer

## Advanced Training Techniques

### Transfer Learning:
- Use pre-trained models as starting point
- Fine-tune for specific tasks
- Dramatically reduces training time and data requirements

### Regularization:
- **Dropout**: Randomly disable neurons during training
- **Batch Normalization**: Normalize layer inputs
- **Weight Decay**: L2 regularization on weights
- **Early Stopping**: Stop training when validation performance plateaus

### Optimization:
- **Adam**: Adaptive learning rates
- **Learning Rate Scheduling**: Adjust rates during training
- **Gradient Clipping**: Prevent exploding gradients
- **Mixed Precision**: Use different floating-point precisions`,
      codeExamples: [
        {
          title: 'Advanced CNN with Transfer Learning',
          language: 'python',
          code: `import tensorflow as tf
from tensorflow import keras
from tensorflow.keras.applications import ResNet50
from tensorflow.keras.layers import GlobalAveragePooling2D, Dense, Dropout
from tensorflow.keras.optimizers import Adam
from tensorflow.keras.callbacks import EarlyStopping, ReduceLROnPlateau
import numpy as np

# Create custom CNN architecture with transfer learning
def create_advanced_cnn(num_classes, input_shape=(224, 224, 3)):
    # Load pre-trained ResNet50 without top layers
    base_model = ResNet50(
        weights='imagenet',
        include_top=False,
        input_shape=input_shape
    )
    
    # Freeze base model layers
    base_model.trainable = False
    
    # Add custom top layers
    model = keras.Sequential([
        base_model,
        GlobalAveragePooling2D(),
        Dense(512, activation='relu'),
        Dropout(0.5),
        Dense(256, activation='relu'),
        Dropout(0.3),
        Dense(num_classes, activation='softmax')
    ])
    
    return model

# Create and compile model
model = create_advanced_cnn(num_classes=10)

model.compile(
    optimizer=Adam(learning_rate=0.001),
    loss='categorical_crossentropy',
    metrics=['accuracy', 'top_k_categorical_accuracy']
)

# Advanced callbacks
callbacks = [
    EarlyStopping(
        monitor='val_accuracy',
        patience=10,
        restore_best_weights=True
    ),
    ReduceLROnPlateau(
        monitor='val_loss',
        factor=0.2,
        patience=5,
        min_lr=1e-7
    )
]

# Model summary
model.summary()

# Fine-tuning strategy
def fine_tune_model(model, base_layers_to_unfreeze=50):
    """Unfreeze top layers of base model for fine-tuning"""
    base_model = model.layers[0]
    base_model.trainable = True
    
    # Freeze all layers except the top ones
    for layer in base_model.layers[:-base_layers_to_unfreeze]:
        layer.trainable = False
    
    # Use lower learning rate for fine-tuning
    model.compile(
        optimizer=Adam(learning_rate=0.0001/10),
        loss='categorical_crossentropy',
        metrics=['accuracy']
    )
    
    return model

print("Advanced CNN with transfer learning created successfully!")`,
          explanation: 'This example demonstrates how to build an advanced CNN using transfer learning with ResNet50, including fine-tuning strategies and advanced training callbacks.'
        }
      ],
      practice: {
        task: 'Build a state-of-the-art image classifier using transfer learning and advanced techniques.',
        starter: `import tensorflow as tf
from tensorflow import keras
from tensorflow.keras.applications import EfficientNetB0
import numpy as np

# Your task: Build an advanced image classifier
# Dataset: CIFAR-10 (10 classes, 32x32 color images)

# TODO:
# 1. Load and preprocess CIFAR-10 dataset
# 2. Use EfficientNetB0 as base model with transfer learning
# 3. Add custom top layers with appropriate regularization
# 4. Implement advanced training strategies (callbacks, learning rate scheduling)
# 5. Evaluate model performance and create visualizations

# Load CIFAR-10
(x_train, y_train), (x_test, y_test) = keras.datasets.cifar10.load_data()

# Your code here...`,
        hints: [
          'Resize CIFAR-10 images to match EfficientNet input size',
          'Use data augmentation to improve generalization',
          'Implement gradual unfreezing for fine-tuning',
          'Monitor multiple metrics during training'
        ]
      }
    },
    {
      id: 'ml-mlops-production',
      title: 'MLOps & Production Machine Learning',
      description: 'Learn to deploy, monitor, and maintain ML models in production environments with modern MLOps practices.',
      duration: '3.5 hours',
      difficulty: 'advanced',
      objectives: [
        'Design ML deployment pipelines',
        'Implement model monitoring and versioning',
        'Set up continuous integration for ML',
        'Handle model drift and updates'
      ],
      content: `# MLOps & Production Machine Learning

MLOps (Machine Learning Operations) brings DevOps practices to machine learning, enabling reliable and scalable ML systems.

## MLOps Fundamentals

### Key Principles:
1. **Automation**: Automate training, testing, and deployment
2. **Reproducibility**: Ensure consistent results across environments
3. **Monitoring**: Track model performance and data quality
4. **Collaboration**: Bridge data science and engineering teams

### MLOps Lifecycle:
1. **Data Management**: Collection, validation, versioning
2. **Model Development**: Experimentation, training, validation
3. **Model Deployment**: Serving, scaling, monitoring
4. **Model Governance**: Compliance, ethics, documentation

## Model Deployment Strategies

### Batch Prediction:
- Process data in batches
- Suitable for non-real-time applications
- Examples: Daily sales forecasts, monthly churn predictions

### Real-time Serving:
- Individual predictions on demand
- Low latency requirements
- Examples: Recommendation systems, fraud detection

### Edge Deployment:
- Models run on edge devices
- Reduced latency and privacy benefits
- Examples: Mobile apps, IoT devices

## Model Monitoring

### Performance Metrics:
- **Accuracy Drift**: Model performance degradation
- **Data Drift**: Input data distribution changes
- **Concept Drift**: Relationship between features and target changes

### Monitoring Infrastructure:
- **Logging**: Capture predictions, features, and outcomes
- **Alerting**: Notify when performance drops
- **Dashboards**: Visualize key metrics and trends

## Continuous Integration/Continuous Deployment (CI/CD) for ML

### CI Pipeline:
1. **Code Testing**: Unit tests, integration tests
2. **Data Validation**: Schema validation, quality checks
3. **Model Testing**: Performance benchmarks, bias checks
4. **Infrastructure Testing**: Deployment environment validation

### CD Pipeline:
1. **Model Packaging**: Container images, artifacts
2. **Staging Deployment**: Test in production-like environment
3. **Production Deployment**: Gradual rollout strategies
4. **Rollback Mechanisms**: Quick reversion if issues occur

## Model Versioning and Experiment Tracking

### Version Control:
- **Code**: Git for source code
- **Data**: DVC, Git LFS for datasets
- **Models**: MLflow, Weights & Biases for model artifacts

### Experiment Tracking:
- **Hyperparameters**: Model configuration
- **Metrics**: Performance measurements
- **Artifacts**: Models, plots, logs
- **Metadata**: Environment, dependencies

## Production Challenges

### Scalability:
- **Horizontal Scaling**: Multiple model instances
- **Vertical Scaling**: More powerful hardware
- **Auto-scaling**: Dynamic resource allocation

### Latency Optimization:
- **Model Compression**: Pruning, quantization
- **Caching**: Store frequent predictions
- **Batch Processing**: Group multiple requests

### Security:
- **Model Protection**: Prevent model theft
- **Data Privacy**: Encrypt sensitive information
- **Access Control**: Authentication and authorization`,
      codeExamples: [
        {
          title: 'Complete MLOps Pipeline with MLflow',
          language: 'python',
          code: `import mlflow
import mlflow.sklearn
from sklearn.ensemble import RandomForestClassifier
from sklearn.model_selection import train_test_split
from sklearn.metrics import accuracy_score, classification_report
from sklearn.datasets import make_classification
import joblib
import logging
import json
from datetime import datetime

# Set up MLflow tracking
mlflow.set_tracking_uri("http://localhost:5000")  # MLflow server
mlflow.set_experiment("production_ml_pipeline")

class MLPipeline:
    def __init__(self, model_name="customer_churn_model"):
        self.model_name = model_name
        self.model = None
        self.logger = self._setup_logging()
    
    def _setup_logging(self):
        """Set up logging for the pipeline"""
        logging.basicConfig(level=logging.INFO)
        return logging.getLogger(__name__)
    
    def train_model(self, X_train, y_train, hyperparameters):
        """Train model with experiment tracking"""
        with mlflow.start_run():
            # Log hyperparameters
            mlflow.log_params(hyperparameters)
            
            # Train model
            self.model = RandomForestClassifier(**hyperparameters)
            self.model.fit(X_train, y_train)
            
            # Log model
            mlflow.sklearn.log_model(
                self.model, 
                "model",
                registered_model_name=self.model_name
            )
            
            self.logger.info(f"Model trained with params: {hyperparameters}")
            
            return self.model
    
    def validate_model(self, X_test, y_test):
        """Validate model and log metrics"""
        predictions = self.model.predict(X_test)
        accuracy = accuracy_score(y_test, predictions)
        
        # Log metrics
        mlflow.log_metric("accuracy", accuracy)
        mlflow.log_metric("n_test_samples", len(X_test))
        
        # Log detailed classification report
        report = classification_report(y_test, predictions, output_dict=True)
        mlflow.log_dict(report, "classification_report.json")
        
        self.logger.info(f"Model validation accuracy: {accuracy:.4f}")
        
        return accuracy
    
    def deploy_model(self, stage="Production"):
        """Deploy model to specified stage"""
        client = mlflow.tracking.MlflowClient()
        
        # Get latest model version
        latest_version = client.get_latest_versions(
            self.model_name, 
            stages=["None"]
        )[0]
        
        # Transition to production
        client.transition_model_version_stage(
            name=self.model_name,
            version=latest_version.version,
            stage=stage
        )
        
        self.logger.info(f"Model v{latest_version.version} deployed to {stage}")
    
    def monitor_predictions(self, X, predictions):
        """Monitor model predictions for drift detection"""
        monitoring_data = {
            "timestamp": datetime.now().isoformat(),
            "n_predictions": len(predictions),
            "feature_means": X.mean().to_dict() if hasattr(X, 'mean') else None,
            "prediction_distribution": {
                "class_0": int((predictions == 0).sum()),
                "class_1": int((predictions == 1).sum())
            }
        }
        
        # Log monitoring data
        with open("monitoring_log.json", "a") as f:
            f.write(json.dumps(monitoring_data) + "\n")
        
        self.logger.info("Prediction monitoring data logged")

# Example usage
if __name__ == "__main__":
    # Create sample data
    X, y = make_classification(n_samples=1000, n_features=10, random_state=42)
    X_train, X_test, y_train, y_test = train_test_split(X, y, test_size=0.2)
    
    # Initialize pipeline
    pipeline = MLPipeline()
    
    # Train model
    hyperparameters = {
        "n_estimators": 100,
        "max_depth": 10,
        "random_state": 42
    }
    
    pipeline.train_model(X_train, y_train, hyperparameters)
    
    # Validate model
    accuracy = pipeline.validate_model(X_test, y_test)
    
    # Deploy if accuracy is sufficient
    if accuracy > 0.8:
        pipeline.deploy_model()
        
        # Monitor predictions
        predictions = pipeline.model.predict(X_test)
        pipeline.monitor_predictions(X_test, predictions)
    
    print("MLOps pipeline completed successfully!")`,
          explanation: 'This example demonstrates a complete MLOps pipeline including experiment tracking, model versioning, deployment, and monitoring using MLflow.'
        }
      ]
    }
    // Additional modules would be added here
  ]
};
